import { OverflowTooltip, Table, useRole } from "@4uhub/lib4uhub";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { motion } from "framer-motion";
import { memo, useCallback, useMemo, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { IReport } from "../../../models/report";
import { ReportDescriptionButton } from "./ReportDescriptionButton";
import { ReportEditDescriptionButton } from "./ReportEditDescriptionButton";
import { ReportGridView } from "./ReportGridView";
import { isInternalReportType } from "./internalReports";
import {
  getReportExecutionRoute,
  getReportCode,
  getReportTypeContextTranslationKey,
  REPORTS_TRANSLATION_PATH,
  REPORTS_VIEW_MODE_STORAGE_KEY,
  REPORTS_VIEW_MODE_TRANSLATION_PATH,
  reportsViewService,
  TReportsViewMode,
} from "./shared";

interface IReportsPageProps {
  isView?: boolean;
  typeId?: string;
}

interface IReportsViewModeSelectProps {
  viewMode: TReportsViewMode;
  onChangeViewMode: (mode: TReportsViewMode) => void;
}

const ReportsViewModeSelect = memo(
  ({ viewMode, onChangeViewMode }: IReportsViewModeSelectProps) => {
    const { t } = useTranslation();

    const changeHandler = useCallback(
      (_: MouseEvent<HTMLElement>, value: TReportsViewMode | null) => {
        if (value === null) return;
        onChangeViewMode(value);
      },
      [onChangeViewMode],
    );

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          justifyContent: "end",
        }}
      >
        <ToggleButtonGroup
          value={viewMode}
          onChange={changeHandler}
          size="small"
          color="primary"
          exclusive
        >
          <Tooltip title={t(REPORTS_VIEW_MODE_TRANSLATION_PATH + "list")}>
            <ToggleButton value="list">
              <FormatListBulletedRoundedIcon />
            </ToggleButton>
          </Tooltip>
          <Tooltip title={t(REPORTS_VIEW_MODE_TRANSLATION_PATH + "grid")}>
            <ToggleButton value="grid">
              <GridViewRoundedIcon />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>
    );
  },
);

const ReportsPage: React.FC<IReportsPageProps> = ({
  isView = true,
  typeId: typeIdProp,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { typeId: routeTypeId } = useParams<{ typeId: string }>();
  const typeId = typeIdProp ?? routeTypeId;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down("md"));

  const [viewMode, setViewMode] = useState<TReportsViewMode>(() => {
    const storedViewMode = localStorage.getItem(
      REPORTS_VIEW_MODE_STORAGE_KEY,
    ) as TReportsViewMode | null;

    return storedViewMode ?? "list";
  });

  const updatePermission = useRole(["Update"]);
  const canEditDescription = updatePermission && !isInternalReportType(typeId);
  const [refreshKey, setRefreshKey] = useState(0);

  const descriptionSavedHandler = useCallback(
    () => setRefreshKey((prev) => prev + 1),
    [],
  );

  const columns: GridColDef<IReport>[] = useMemo(
    () => [
      ...(canEditDescription
        ? [
            {
              field: "edit",
              headerName: "",
              maxWidth: 48,
              sortable: false,
              disableColumnMenu: true,
              renderCell: (params) => (
                <ReportEditDescriptionButton
                  report={params.row}
                  onSaved={descriptionSavedHandler}
                />
              ),
            } as GridColDef<IReport>,
          ]
        : []),
      {
        field: "description",
        headerName: "",
        maxWidth: 48,
        sortable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const description = params.row.description?.trim();

          if (!description) return null;

          return <ReportDescriptionButton description={description} />;
        },
      },
      {
        field: "code",
        headerName: t(REPORTS_TRANSLATION_PATH + "code") || "Code",
        maxWidth: 100,
        renderCell: (params) => {
          const value = getReportCode(params.row) ?? "-";

          return (
            <OverflowTooltip
              text={String(value)}
              tooltip={String(value)}
              textTransform="none"
              variant="body2"
            />
          );
        },
      },
      {
        field: "name",
        headerName: t(REPORTS_TRANSLATION_PATH + "name") || "Name",
        flex: 1,
        renderCell: (params) => {
          const value = params.row.name ?? "-";

          return (
            <OverflowTooltip
              text={value}
              tooltip={value}
              textTransform="none"
              variant="body2"
            />
          );
        },
      },
    ],
    [canEditDescription, descriptionSavedHandler, t],
  );

  const serviceProps = useMemo(
    () => ({
      ReportTypeId: typeId ?? "",
    }),
    [typeId],
  );

  const reportsPageTitle = useMemo(() => {
    const baseTitle = t(REPORTS_TRANSLATION_PATH + "title");
    const reportTypeTranslationKey = getReportTypeContextTranslationKey(typeId);

    if (!reportTypeTranslationKey) return baseTitle;

    return `${baseTitle} - ${t(reportTypeTranslationKey)}`;
  }, [t, typeId]);

  const changeViewModeHandler = useCallback((mode: TReportsViewMode) => {
    localStorage.setItem(REPORTS_VIEW_MODE_STORAGE_KEY, mode);
    setViewMode(mode);
  }, []);

  const handleReportClick = useCallback(
    (report: IReport) => {
      if (!typeId) return;

      const reportCode = getReportCode(report);
      const searchParams = new URLSearchParams();

      if (reportCode !== undefined && reportCode !== null) {
        searchParams.set("reportCode", String(reportCode));
      }

      if (report.name) {
        searchParams.set("reportName", report.name);
      }

      const queryString = searchParams.toString();
      const reportRoute = getReportExecutionRoute(typeId, report.id);

      navigate(`${reportRoute}${queryString ? `?${queryString}` : ""}`, {
        relative: "path",
        state: {
          reportCode,
          reportName: report.name,
        },
      });
    },
    [navigate, typeId],
  );

  const handleRowClick = useCallback(
    (params: GridRowParams<IReport>) => {
      handleReportClick(params.row);
    },
    [handleReportClick],
  );

  if (!typeId) return null;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <ReportsViewModeSelect
        viewMode={viewMode}
        onChangeViewMode={changeViewModeHandler}
      />

      <motion.div
        key={`${typeId}-${viewMode}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.6, -0.05, 0.01, 0.99] }}
        style={{
          height:
            viewMode === "grid"
              ? matches
                ? "calc(100vh - 16rem)"
                : "calc(100vh - 12rem)"
              : "95%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {viewMode === "list" ? (
          <Box
            sx={{
              height: "100%",
              minHeight: 0,
              "& .MuiDataGrid-columnHeaderCheckbox, & .MuiDataGrid-cellCheckbox":
                { display: "none !important" },
            }}
          >
            <Table<IReport>
              key={`${typeId}-${refreshKey}`}
              showDefaultMacroFilters={false}
              showDeleteButton={!isView}
              showButton={!isView}
              defaultActions={!isView}
              service={reportsViewService}
              serviceProps={serviceProps}
              columns={columns}
              title={reportsPageTitle}
              searchInput
              onRowClick={handleRowClick}
              searchInputPlaceHolder={
                t(REPORTS_TRANSLATION_PATH + "search") || "Search for a report"
              }
            />
          </Box>
        ) : (
          <ReportGridView
            key={refreshKey}
            typeId={typeId}
            title={reportsPageTitle}
            onReportClick={handleReportClick}
            canEditDescription={canEditDescription}
            onDescriptionSaved={descriptionSavedHandler}
          />
        )}
      </motion.div>
    </Box>
  );
};

export default memo(ReportsPage);
